import { useId, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export type ToggleTone = 'ngahere' | 'pohutukawa' | 'wahine' | 'danger';

export interface ToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** The colour the track takes when on. Off is always the sand track. */
  tone?: ToggleTone;
  label: ReactNode;
  /** Second line under the label — used by "Recurring trip". */
  description?: ReactNode;
  /** Small icon before the label, as the filter switches have. */
  icon?: ReactNode;
  disabled?: boolean;
  className?: string;
}

const TONE: Record<ToggleTone, string> = {
  ngahere: 'bg-ngahere',
  pohutukawa: 'bg-pohutukawa',
  wahine: 'bg-wahine',
  danger: 'bg-danger',
};

/**
 * The 38×22 switch used by every boolean in the product — search filters,
 * publish preferences, recurring trips.
 */
export function Toggle({
  checked,
  onCheckedChange,
  tone = 'ngahere',
  label,
  description,
  icon,
  disabled = false,
  className,
}: ToggleProps) {
  const labelId = useId();

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-3 py-[9px] text-left',
        'disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
    >
      <span id={labelId} className="flex items-center gap-2">
        {icon}
        <span>
          <span className="block text-body-sm font-medium">{label}</span>
          {description ? <span className="block text-meta text-subtle">{description}</span> : null}
        </span>
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'relative inline-block h-[22px] w-[38px] flex-none rounded-full',
          'transition-colors duration-fast ease-standard',
          checked ? TONE[tone] : 'bg-track',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-knob',
            'transition-[left] duration-fast ease-standard',
            checked ? 'left-[18px]' : 'left-0.5',
          )}
        />
      </span>
    </button>
  );
}
